import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Autocomplete, Button, Checkbox, Dialog, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, Stack, TextField, Typography, useTheme } from '@mui/material'
import CloseModalButton from '../../../../components/CloseModalButton'
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from '../../../../components/Dialog.styled'
import dayjs, { Dayjs } from 'dayjs'
import styled from '@emotion/styled'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { TeamMember } from '../../../../types'
import { getFullNameOrEmail, User } from '../../../../redux/user'
import { FieldValidation, Task, TaskFormData, TaskModalState, TaskPayload, WeekDay } from '../types'
import { ToolTip } from '../../dentalenhancements/components/ToolTip'
import { TaskFrequency } from '../enums'
import * as service from '../service'
import { getPracticeTasks } from '../slice'
import { useAppDispatch } from '../../../../redux/store'
import { addError, addSuccess } from '../../../../redux/alerts'
import { cronToModel, days, getFormValidation, getRequiredFields, getTaskPayload, getIsValidForm, weekdays } from '../utils/helpers'

interface TaskModalProps {
  open: boolean
  users: TeamMember[]
  task: Task | null
  currentUser: User
  customerNumber: string,
  setTaskModalOpen: Dispatch<SetStateAction<boolean>>,
  setSelectedTask: Dispatch<SetStateAction<Task | null>>,
}

const initialState: TaskModalState = {
  formData: {
    description: '',
    assignedTo: '',
    frequency: TaskFrequency.OneTime,
    startDate: dayjs(),
    active: true,
    cron: null,
    customer_number: '',
    endDate: null,
    name: '',
    reminder: false,
    weekly: '',
    noEndDate: true,
    weekdays: [],
    dayOfMonth: '',
  },
  validations: {
    startDate: {
      isValid: true,
      message: null,
    },
    assignedTo: {
      isValid: true,
      message: null,
    },
    weekly: {
      isValid: true,
      message: null,
    },
    name: {
      isValid: true,
      message: null,
    },
    dayOfMonth: {
      isValid: true,
      message: null,
    },
    weekdays: {
      isValid: true,
      message: null,
    },
    endDate: {
      isValid: true,
      message: null,
    },
  }
}

const StyledText = styled(Typography)(({ theme }) => ({ fontSize: '13px', fontFamily: "'Open Sans', sans-serif" }))

const TaskModal = (props: TaskModalProps) => {
  const {
    open,
    users,
    task,
    currentUser,
    customerNumber,
    setTaskModalOpen,
    setSelectedTask,
  } = props
  const minStartDate = dayjs()
  const theme = useTheme()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState<TaskFormData>(initialState.formData)
  const [formErrors, setFormErrors] = useState<FieldValidation>(initialState.validations)

  const dispatch = useAppDispatch()

  const handleClose = () => {
    setFormData(initialState.formData)
    setFormErrors(initialState.validations)
    setLoading(false)

    setTaskModalOpen(false)
    setSelectedTask(null)
  }

  const handleSubmit = async () => {
    const requiredFields = getRequiredFields(formData)
    const formValidation = getFormValidation(task, formData)
    const isValidForm = getIsValidForm(requiredFields, formValidation)
    
    setFormErrors(formValidation)

    if (!isValidForm) return

    try {
      setLoading(true)

      const payload: TaskPayload = getTaskPayload(customerNumber, currentUser, task, formData)

      if (task) {
        await service.updateTask(customerNumber, task.id, payload)
      } else {
        await service.createTask(customerNumber, payload)
      }

      await dispatch(getPracticeTasks(customerNumber))
      
      const message = `Your task has successfully been ${task ? 'updated' : 'created'}.`

      dispatch(addSuccess(message))
    } catch (error) {
      console.error('Error saving task:', error)
      const message = `Failed to ${task ? 'update' : 'create'} task. Please try again.`
      dispatch(addError(message))
    } finally {
      handleClose()
    }    
  }

  const handleFormChange = (field: string, value: string | boolean | number | null | string[] | Dayjs | TaskFrequency) => {
    if (field === 'frequency') setFormErrors(initialState.validations)
    setFormData({ ...formData, [field]: value })
  }

  const minEndDate = useMemo(() => {
    const baseline = formData.startDate ?? minStartDate
    return dayjs(baseline).clone().add(1, 'day')
  }, [formData.startDate?.valueOf()])

  const assignableUsers = useMemo(() => {
    return users
      .filter((user) => user.account_active || String(user.key_) === formData.assignedTo)
      .sort((a, b) => getFullNameOrEmail(a).localeCompare(getFullNameOrEmail(b)))
  }, [users, formData.assignedTo])

  useEffect(() => {
    if (task) {
      const { frequency: selectedFrequency, repeats } = cronToModel(task)

      const selectedWeekdays = selectedFrequency === TaskFrequency.Weekday
        ? repeats as WeekDay[]
        : [] as WeekDay[]

      const formData: TaskFormData = {
        description: task.description ?? '',
        assignedTo: String(task.assignedTo),
        frequency: selectedFrequency,
        startDate: dayjs(task.startDate),
        active: task.active,
        cron: task.cron,
        customer_number: task.customer_number,
        endDate: task.endDate ? dayjs(task.endDate) : null,
        name: task.name ?? '',
        reminder: task.reminder,
        weekly: String(repeats),
        noEndDate: !Boolean(task.endDate),
        weekdays: selectedWeekdays,
        dayOfMonth: selectedFrequency === TaskFrequency.Monthly && repeats ? String(repeats) : '',
      }

      setFormData(formData)
    }
  }, [task])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      sx={{ color: theme.palette.color50555b.main }}
      fullWidth
    >
      <CloseModalButton handleClose={handleClose} />
      <StyledDialogTitle>{task ? 'Edit' : 'Add'} Task</StyledDialogTitle>
      <StyledDialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth size="small">
            <TextField
              size="small"
              label="Title"
              value={formData.name}
              placeholder='Required'
              onChange={(e) => handleFormChange('name', e.target.value)}
              fullWidth
              required
              error={!formErrors.name.isValid}
              helperText={formErrors.name.message}
            />
          </FormControl>

          <TextField
            size="small"
            label="Description"
            value={formData.description}
            placeholder="Type your message"
            multiline
            rows={4}
            onChange={(e) => handleFormChange('description', e.target.value)}
            fullWidth
          />

          <FormControl size="small">
            <InputLabel id="assign-to-label">Assign To</InputLabel>
            <Select
              labelId="assign-to-label"
              label="Assign To"
              value={formData.assignedTo}
              onChange={(e) => handleFormChange('assignedTo', e.target.value)}
              fullWidth
              required
              error={!formErrors.assignedTo.isValid}
            >
              {assignableUsers.map((user) => (
                <MenuItem key={user.key_} value={String(user.key_)}>
                  {getFullNameOrEmail(user)}
                </MenuItem>
              ))}
            </Select>
            {formErrors.assignedTo.message && <FormHelperText error>{ formErrors.assignedTo.message }</FormHelperText>}
          </FormControl>

          <FormControl size='small'>
            <FormLabel sx={{ fontSize: '13px' }} id="frequency-label">Frequency</FormLabel>
            <RadioGroup
              aria-labelledby="frequency-label"
              defaultValue="All"
              name="frequency"
              onChange={(e) => handleFormChange('frequency', e.target.value)}
              sx={{ display: 'block' }}
            >
              {Object.values(TaskFrequency).map(f => (
                <FormControlLabel key={f} value={f} control={<Radio checked={formData.frequency === f} />} label={<StyledText>{f}</StyledText>} />
              ))}
            </RadioGroup>
          </FormControl>

          {formData.frequency === TaskFrequency.Weekday ? (
            <FormControl fullWidth size="small">
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={Array.from(weekdays.keys())}
                value={formData.weekdays}
                onChange={(_, newValue) => handleFormChange('weekdays', newValue)}
                renderOption={(props, option, { selected }) => {
                  const { key, ...rest } = props

                  return (
                    <li key={key} {...rest} >
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option}
                    </li>
                  )
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    label="Repeats"
                    error={!formErrors.weekdays.isValid}
                    helperText={formErrors.weekdays.message}                    
                  />
                )}
                fullWidth
              />
            </FormControl>
          ) : null}

          {formData.frequency === TaskFrequency.Weekly ? (
            <FormControl fullWidth size="small" sx={{ display: 'flex', flexDirection: 'unset', alignItems: 'center' }}>
              <div><StyledText>Repeats Every</StyledText></div>
              <TextField
                size="small"
                onChange={(event) => handleFormChange('weekly', event.target.value)}
                value={formData.weekly}
                type='number'
                required
                error={!formErrors.weekly.isValid}
                sx={{ width: '70px', marginLeft: '10px', marginRight: '10px' }}
              />
              <div><StyledText>Week(s)</StyledText></div>
              {formErrors.weekly.message && <FormHelperText error>{ formErrors.weekly.message }</FormHelperText>}
            </FormControl>
          ) : null}

          {formData.frequency === TaskFrequency.Monthly ? (
            <FormControl size="small">
              <InputLabel id="repeats-label">Repeats</InputLabel>
              <Select
                labelId="repeats-label"
                label="Repeats"
                value={formData.dayOfMonth}
                onChange={(e) => handleFormChange('dayOfMonth', e.target.value)}
                fullWidth
                required
                error={!formErrors.dayOfMonth.isValid}
              >
                {days.map((day) => (
                  <MenuItem key={day} value={day}>
                    {day}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.dayOfMonth.message && <FormHelperText error>{ formErrors.dayOfMonth.message }</FormHelperText>}
            </FormControl>
          ) : null}

          <FormControl fullWidth size="small">
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="en"
            >
              <DatePicker
                label='Start Date'
                onChange={(newDate) => {
                  handleFormChange('startDate', newDate)
                }}
                format="MM-DD-YYYY"
                views={['year', 'month', 'day']}
                disablePast
                disabled={Boolean(task)}
                value={formData.startDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    error: !formErrors.startDate.isValid,
                    helperText: formErrors.startDate.message,                    
                  }
                }}
              />
            </LocalizationProvider>
          </FormControl>

          {formData.frequency !== TaskFrequency.OneTime ? (
            <div className='flex'>
              <FormControl fullWidth size="small">
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="en"
                >
                  <DatePicker
                    label='End Date'
                    onChange={(newDate) => {
                      handleFormChange('endDate', newDate)
                    }}
                    format="MM-DD-YYYY"
                    views={["year", "month", "day"]}
                    disablePast
                    disabled={formData.noEndDate}
                    minDate={minEndDate}
                    value={formData.endDate}
                    slotProps={{
                      textField: {
                        size: 'small',
                        error: !formErrors.endDate.isValid,
                        helperText: formErrors.endDate.message,
                      }
                    }}
                  />
                </LocalizationProvider>
              </FormControl>
              <FormGroup sx={{ padding: '7px', marginLeft: '15px' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ mr: 1 }}
                      checked={formData.noEndDate}
                      onChange={(e) => handleFormChange('noEndDate', e.target.checked)}
                    />
                  }
                  label={<StyledText>Never</StyledText>}
                />
              </FormGroup>
            </div>
          ) : null}

          <FormGroup sx={{ padding: '7px' }}>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{ mr: 1 }}
                  checked={formData.reminder}
                  onChange={(e) => handleFormChange('reminder', e.target.checked)}
                />
              }
              label={
                <StyledText>
                  <ToolTip helpText='Overdue notifications are sent to the person who assigned the task when a task is still incomplete 48 hours after the task is due.' />
                  {' '}Receive Overdue Notices
                </StyledText>
              }
            />
            <StyledText>
              * Only users who have opted in for communication will receive notifications.
            </StyledText>
          </FormGroup>
        </Stack>
      </StyledDialogContent>
      <StyledDialogFooter>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button disabled={loading} variant="contained" onClick={handleSubmit}>
          Save
        </Button>
      </StyledDialogFooter>
    </Dialog>
  )
}

export default TaskModal